import { Injectable, inject } from '@angular/core';
import { SeedApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/seed/seed-api';
import { PlantApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/plant/plant-api';
import { PosthogService } from './posthog.service';
import { StorageService } from './storage.service';

/** Nombre de sachets et de plantes du compte. */
export interface GardenElementCount {
  seeds: number;
  plants: number;
}

/**
 * Rang d'un élément de jardin (plante ou sachet) : 1 pour le premier, 2, 3…
 *
 * Mesure du démarrage (CR-08) : la part des comptes qui atteignent leur 3e
 * élément se lit sur `seed_added` et `plant_added`. Le rang est le nombre
 * d'éléments du compte APRÈS l'ajout, lu sur les totaux des listes (une ligne
 * demandée, seul le total sert).
 */
@Injectable({ providedIn: 'root' })
export class GardenRankService {
  // Les classes du SDK lisent leur configuration dans un singleton : on les
  // instancie directement, comme `PremiumService`.
  private readonly seedApi = new SeedApi();
  private readonly plantApi = new PlantApi();
  private readonly posthog = inject(PosthogService);
  private readonly storage = inject(StorageService);

  /**
   * Éléments de jardin du compte : sachets et plantes, lus sur les totaux des
   * listes (une ligne demandée, seul le total sert). Une seule source pour le
   * rang des événements et pour les lignes de démarrage de l'accueil. Null
   * quand les totaux ne se lisent pas.
   */
  async count(): Promise<GardenElementCount | null> {
    try {
      const lang = this.storage.getCurrentLanguage();
      const [seeds, plants] = await Promise.all([
        this.seedApi.list({} as any, 1, 1, lang),
        this.plantApi.list({} as any, 1, 1, lang),
      ]);
      return { seeds: seeds.page?.totalRecords ?? 0, plants: plants.page?.totalRecords ?? 0 };
    } catch {
      return null;
    }
  }

  /**
   * Envoie l'événement avec son rang. Ne bloque jamais l'écran (appel sans
   * attente) et ne perd jamais l'événement : sans total lisible, il part sans
   * rang.
   */
  async capture(event: 'seed_added' | 'plant_added', properties: Record<string, any> = {}): Promise<void> {
    const count = await this.count();
    const rank = count ? count.seeds + count.plants : null;
    this.posthog.capture(event, { ...properties, rank });
  }
}
